import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services';
import { MediaFolder, MediaLibraryResponse } from './types';

const uploadImage = async (file: File, path = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('path', path);

  // Using multipart/form-data for file upload
  const response = await api.post('media-library/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

const createFolder = async (path: string, name: string): Promise<MediaFolder> => {
  const response = await api.post('media-library/folders', { path, name });
  return response.data;
};

// React Query hooks
export const useImages = (path = '') =>
  useQuery<MediaLibraryResponse>({
    // `path` goes through as a query parameter so axios encodes it: folder names containing
    // spaces (e.g. "fall-fsg 2026") previously broke when concatenated into the URL.
    queryKey: ['images', path],
    queryFn: () => api.get('media-library/items', { params: { path } }).then((res) => res.data),
  });

export const useUploadImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, path }: { file: File; path: string }) => uploadImage(file, path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, name }: { path: string; name: string }) => createFolder(path, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
};
